import React, {type JSX} from 'react';
import {type DropzoneRef, useDropzone} from 'react-dropzone';
import {Box, Button, Divider, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import {wrapAsyncFunction} from '../../utils/wrapAsyncFunction';

const StyledDropzoneContainer = styled('div')<{
  isDragAccept: boolean;
  hasFiles: boolean;
}>(({theme, isDragAccept, hasFiles}) => ({
  borderRadius: '8px',
  padding: '30px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: '4px',
  borderStyle: 'dashed',
  minHeight: hasFiles ? '160px' : '300px',
  ...(isDragAccept
    ? {
        backgroundColor: `${theme.palette.secondary.main}10`,
        borderColor: theme.palette.secondary.main,
      }
    : {
        borderColor: theme.palette.divider,
      }),
}));

function FixedDivider() {
  return (
    <Box
      marginTop="16px"
      marginBottom="16px"
      marginLeft="auto"
      marginRight="auto"
      width="128px"
    >
      <Divider />
    </Box>
  );
}

function DropZoneScreen({
  open,
  isDragAccept,
  hasFiles,
}: DropzoneRef & {isDragAccept: boolean; hasFiles: boolean}): JSX.Element {
  return (
    <Box color="textSecondary" textAlign="center">
      <Typography
        variant="subtitle1"
        component="p"
        marginBottom={1}
        color={isDragAccept ? 'secondary.main' : 'text.secondary'}
      >
        Drag and drop your files here. Accepted file formats: <br />
        <b>.ccd, .сcda, .dcm, .jpeg, .jpg, .pdf, .png, .xml, .zip</b>
      </Typography>
      {isDragAccept ? null : (
        <>
          <FixedDivider />
          <Typography
            variant="subtitle1"
            marginBottom={1}
            component="p"
            color="textSecondary"
          >
            OR
          </Typography>
        </>
      )}
      {isDragAccept ? null : (
        <Button
          size="presizedSmall"
          variant="contained"
          onClick={open}
          data-testid="click-to-browse-btn"
        >
          {hasFiles ? 'Click to Browse' : 'Browse on Device'}
        </Button>
      )}
    </Box>
  );
}

type Props = {
  hasFiles: boolean;
  onDrop: (files: File[]) => Promise<void>;
};
export function DropZone({hasFiles, onDrop}: Readonly<Props>): JSX.Element {
  const {getRootProps, getInputProps, open, isDragAccept} = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: wrapAsyncFunction(onDrop),
  });

  const rootProps = getRootProps();

  return (
    <StyledDropzoneContainer
      {...rootProps}
      isDragAccept={isDragAccept}
      hasFiles={hasFiles}
    >
      <input data-testid="dropzone" {...getInputProps()} />
      <DropZoneScreen
        open={open}
        isDragAccept={isDragAccept}
        hasFiles={hasFiles}
      />
    </StyledDropzoneContainer>
  );
}
